import isEmail from 'validator/lib/isEmail';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Form, Input, Grid, Message } from 'semantic-ui-react';
import { useDidUpdate, useToggle } from '../../lib/hooks';
import { login } from '../../store/auth';
import { useForm } from '../../hooks';
import { isUsername } from '../../utils/validator';
import Logo from '../Logo/Logo';
import styles from './Login.module.scss';
import { removeAccessToken, setAccessToken } from '../../utils/access-token-storage';
import { fetchUser, setError } from '../../store/auth';

const createMessage = (error) => {
  if (!error) {
    return error;
  }

  switch (error) {
    case 'Invalid email or username':
      return {
        type: 'error',
        content: '无效的用户或密码',
      };
    case 'Invalid activation':
      return {
        type: 'error',
        content: '无效的用户或密码',
      };
    case 'Invalid password':
      return {
        type: 'error',
        content: '无效的用户或密码',
      };
    case 'Failed to fetch':
      return {
        type: 'warning',
        content: '没有网络连接',
      };
    case 'Network request failed':
      return {
        type: 'warning',
        content: '服务连接失败',
      };
    case 'No backend permission':
      return {
        type: 'warning',
        content: '无效的用户或密码',
      };
    default:
      return {
        type: 'warning',
        content: '未知错误，请稍后重试',
      };
  }
};
const defaultData = {
  emailOrUsername: '',
  password: '',
};
const email = '';
const Login = React.memo(() => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const error = useSelector((state) => state.auth.error);
  const loading = useSelector((state) => state.auth.loading);
  const [data, handleFieldChange, setData] = useForm(() => {
    if (email) {
      return {
        password: '',
        ...defaultData,
        emailOrUsername: email,
      };
    }
    return {
      emailOrUsername: '',
      password: '',
      ...defaultData,
    };
  });

  const message = useMemo(() => createMessage(error), [error]);
  const [focusPasswordFieldState, focusPasswordField] = useToggle();

  const emailOrUsernameField = useRef(null);
  const passwordField = useRef(null);

  const handleSubmit = useCallback(async () => {
    const cleanData = {
      ...data,
      emailOrUsername: data.emailOrUsername.trim(),
    };

    if (!isEmail(cleanData.emailOrUsername) && !isUsername(cleanData.emailOrUsername)) {
      emailOrUsernameField.current.select();
      return;
    }

    if (!cleanData.password) {
      passwordField.current.focus();
      return;
    }

    const resultAction = await dispatch(login(cleanData));

    if (login.fulfilled.match(resultAction)) {
      setAccessToken(resultAction.payload);
      const userResult = await dispatch(fetchUser());
      if (fetchUser.fulfilled.match(userResult)) {
        const user = userResult.payload;
        if (user.isAnnouncementAdmin) {
          navigate('/');
        } else {
          removeAccessToken();
          dispatch(setError('No backend permission'));
        }
      }
    }
  }, [data, navigate, dispatch]);

  useEffect(() => {
    if (emailOrUsernameField.current) {
      emailOrUsernameField.current.focus();
    }
  }, []);

  useEffect(() => {
    if (error) {
      switch (error.message) {
        case 'Invalid email or username':
          emailOrUsernameField.current.select();

          break;
        case 'Invalid password':
          setData((prevData) => ({
            ...prevData,
            password: '',
          }));
          focusPasswordField();
          break;
        default:
      }
    }
  }, [setData, focusPasswordField, error]);

  useDidUpdate(() => {
    passwordField.current.focus();
  }, [focusPasswordFieldState]);

  return (
    <div className={classNames(styles.wrapper, styles.fullHeight)}>
      <Grid verticalAlign="middle" className={styles.fullHeightPaddingFix}>
        <Grid.Column widescreen={16} largeScreen={16} computer={16} tablet={16} mobile={16}>
          <Grid verticalAlign="middle" className={styles.fullHeightPaddingFix}>
            <Grid.Column>
              <div className={styles.loginWrapper}>
                <div className={styles.logo}>
                  <Logo />
                </div>
                <div>
                  {message && (
                    <Message
                      {...{
                        [message.type]: true,
                      }}
                      visible
                      content={message.content}
                    />
                  )}
                  <Form size="large" onSubmit={handleSubmit}>
                    <div className={styles.inputWrapper}>
                      <div className={styles.inputLabel}>邮箱</div>
                      <Input
                        fluid
                        ref={emailOrUsernameField}
                        name="emailOrUsername"
                        value={data.emailOrUsername}
                        className={styles.input}
                        onChange={handleFieldChange}
                      />
                    </div>
                    <div className={styles.inputWrapper}>
                      <div className={styles.inputLabel}>密码</div>
                      <Input.Password
                        fluid
                        ref={passwordField}
                        name="password"
                        value={data.password}
                        className={styles.input}
                        onChange={handleFieldChange}
                      />
                    </div>
                    <Form.Button
                      primary
                      size="large"
                      icon="right arrow"
                      labelPosition="right"
                      content="登陆"
                      floated="right"
                      loading={loading}
                      disabled={loading}
                    />
                  </Form>
                </div>
              </div>
            </Grid.Column>
          </Grid>
        </Grid.Column>
      </Grid>
    </div>
  );
});

export default Login;
